import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CompanyService } from '../../../shared/services/company.service';
import { AuthService } from '../../../shared/services/auth.service';
import { LoaderComponent } from '../../../components/loader/loader.component';

@Component({
  selector: 'app-company-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
})
export class CompanyFormComponent implements OnInit {
  @Output() companyAdded = new EventEmitter<void>();
  companyForm: FormGroup;
  isSubmitting = false;
  isNew = true;
  companyId: string | null = null;
  userId: string = '';
  fieldErrors: { [key: string]: string } = {};
  isLoading = false;
  constructor(
    private fb: FormBuilder,
    private companyService: CompanyService,
    private toastr: ToastrService,
    public router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {
    this.companyForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      companyNameArabic: ['', Validators.maxLength(100)],
      crNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      policyStartDate: ['', Validators.required],
      sponsorID: ['', [Validators.pattern(/^\d*$/), Validators.maxLength(20)]], // Allow empty
      vatNumber: ['', Validators.pattern(/^[0-9]{0,15}$/)], // Allow empty or up to 15 digits
      contactEmail: ['', [Validators.email, Validators.maxLength(100)]],
      contactPhone: [
        '',
        [
          this.optionalSaudiPhoneNumberValidator, // Only validate if not empty
        ],
      ],
      bankNameID: ['', [Validators.pattern(/^\d*$/), Validators.maxLength(10)]], // Allow empty
      ibanNumber: ['', [Validators.maxLength(30), this.optionalIbanValidator]], // Only validate if not empty
      numberOfEmployees: [
        '',
        [Validators.pattern(/^\d*$/), Validators.max(100000)], // Allow empty
      ],
      entity700Number: ['', [Validators.maxLength(50)]],
    });

    this.fieldErrors = {
      name: 'Company name is required (max 100 chars)',
      companyNameArabic: 'Maximum 100 characters allowed',
      crNumber: 'CR Number is required (numbers only)',
      policyStartDate: 'Policy start date is required',
      sponsorID: 'Must be a valid number (max 20 digits)',
      vatNumber: 'Must be 15 digits',
      contactEmail: 'Please enter a valid email (max 100 chars)',
      contactPhone: 'Please enter a valid Saudi phone number',
      bankNameID: 'Must be a valid number (max 10 digits)',
      ibanNumber: 'Please enter a valid IBAN number',
      numberOfEmployees: 'Must be a valid number (max 100,000)',
      entity700Number: 'Maximum 50 characters allowed',
    };
  }

  ngOnInit(): void {
    this.userId = this.getUserIdFromLocalStorage();

    this.route.queryParams.subscribe((params) => {
      this.isNew = params['isNew'] !== 'false';
      this.companyId = params['companyId'];
      if (this.companyId) {
        this.isNew = false;
        this.loadCompanyData(this.companyId);
      }
    });

    // Clear server errors when user starts typing
    this.companyForm.valueChanges.subscribe(() => {
      this.clearServerErrors();
    });
  }

  private saudiPhoneNumberValidator(control: AbstractControl) {
    if (!control.value) return null;
    const regex = /^(?:\+966|966|0)?5[0-9]{8}$/;
    return regex.test(control.value) ? null : { invalidSaudiPhone: true };
  }

  private optionalSaudiPhoneNumberValidator(control: AbstractControl) {
    if (!control.value || control.value.trim() === '') return null;
    const regex = /^(?:\+966|966|0)?5[0-9]{8}$/;
    return regex.test(control.value) ? null : { invalidSaudiPhone: true };
  }

  private ibanValidator(control: AbstractControl) {
    if (!control.value) return null;
    const regex = /^SA[0-9]{2}[A-Z0-9]{4}[0-9]{14}$/;
    return regex.test(control.value) ? null : { invalidIban: true };
  }

  private optionalIbanValidator(control: AbstractControl) {
    if (!control.value || control.value.trim() === '') return null;
    const regex = /^SA[0-9]{2}[A-Z0-9]{4}[0-9]{14}$/;
    return regex.test(control.value) ? null : { invalidIban: true };
  }

  private getUserIdFromLocalStorage(): string {
    try {
      const keycloakProfile = localStorage.getItem('keycloakUserProfile');
      return keycloakProfile ? JSON.parse(keycloakProfile).id : '';
    } catch (error) {
      console.error('Error parsing Keycloak profile:', error);
      return '';
    }
  }

  loadCompanyData(companyId: string): void {
    console.log('Loading company data for ID:', companyId);
    this.isLoading = true; // Use a separate loading flag to avoid conflicts
    this.companyService.getCompanyByCompanyId(companyId).subscribe({
      next: (company) => {
        console.log('Company data loaded:', company);
        // Store the actual company ID for updates
        this.companyId = company.id?.toString() || null;
        console.log('Set companyId to:', this.companyId);

        // Format the policy start date for the date input
        const formattedCompany = {
          ...company,
          policyStartDate: company.policyStartDate
            ? new Date(company.policyStartDate).toISOString().substring(0, 10)
            : '',
        };
        this.companyForm.patchValue(formattedCompany);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load company data:', err);
        this.isLoading = false;
        this.handleError('Error loading company data', err);
        this.router.navigate(['/dashboard/companies']);
      },
    });
  }

  onSubmit(): void {
    console.log('Form submission started');
    console.log('Form valid:', this.companyForm.valid);
    console.log('Is submitting:', this.isSubmitting);
    console.log('Is new company:', this.isNew);
    console.log('Company ID:', this.companyId);
    console.log('User ID:', this.userId);

    if (this.companyForm.invalid) {
      console.log('Form is invalid, marking all fields as touched');
      this.markFormGroupTouched(this.companyForm);
      return;
    }

    if (this.isSubmitting) {
      console.log('Form is already being submitted, ignoring duplicate submission');
      return;
    }

    this.isSubmitting = true;
    const formData = this.prepareFormData(this.companyForm.value);
    console.log('Prepared form data:', formData);

    if (this.isNew) {
      console.log('Creating new company');
      this.createCompany(formData);
    } else {
      console.log('Updating existing company');
      this.updateCompany(formData);
    }
  }

  private prepareFormData(formData: any): any {
    return {
      ...formData,
      sponsorID: formData.sponsorID ? Number(formData.sponsorID) : null,
      bankNameID: formData.bankNameID ? Number(formData.bankNameID) : null,
      numberOfEmployees: formData.numberOfEmployees
        ? Number(formData.numberOfEmployees)
        : null,
    };
  }

  private createCompany(formData: any): void {
    this.companyService.saveCompanyDetails(formData, this.userId).subscribe({
      next: async () => {
        this.toastr.success('Company created successfully');
        this.companyAdded.emit();
        await this.handleTokenRefresh();
        this.router.navigate(['/dashboard/companies']);
      },
      error: (err) => this.handleError('Error creating company', err),
    });
  }

  private updateCompany(formData: any): void {
    if (!this.companyId) {
      console.error('Company ID is missing for update operation');
      this.toastr.error('Cannot update company: Company ID is missing');
      this.isSubmitting = false;
      return;
    }

    if (!this.userId) {
      console.error('User ID is missing for update operation');
      this.toastr.error('Cannot update company: User ID is missing');
      this.isSubmitting = false;
      return;
    }

    console.log('Updating company with ID:', this.companyId);
    console.log('Form data:', formData);
    console.log('User ID:', this.userId);

    this.companyService
      .updateCompany(this.companyId, formData, this.userId)
      .subscribe({
        next: (response) => {
          console.log('Company update successful:', response);
          this.isSubmitting = false;
          this.toastr.success('Company updated successfully');
          this.companyAdded.emit();
          this.router.navigate(['/dashboard/companies']);
        },
        error: (err) => {
          console.error('Company update failed:', err);
          this.handleError('Error updating company', err);
        },
      });
  }

  private async handleTokenRefresh(): Promise<void> {
    try {
      await this.authService.forceTokenRefresh();
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  }

  private handleError(defaultMessage: string, error: any): void {
    console.error(defaultMessage, error);
    this.isSubmitting = false;

    // Check for network or connection errors
    if (error.status === 0) {
      this.toastr.error('Network error: Please check your internet connection');
      return;
    }

    // Check for authentication errors
    if (error.status === 401) {
      this.toastr.error('Authentication error: Please log in again');
      return;
    }

    // Check for permission errors
    if (error.status === 403) {
      this.toastr.error('Permission denied: You are not authorized to perform this action');
      return;
    }

    // Check for not found errors
    if (error.status === 404) {
      this.toastr.error('Company not found: The company may have been deleted');
      return;
    }

    // Check for server errors
    if (error.status === 500) {
      this.toastr.error('Server error: Please try again later');
      return;
    }

    // Handle validation errors
    if (error?.error?.errors) {
      Object.keys(error.error.errors).forEach((key) => {
        const control = this.companyForm.get(key);
        if (control) {
          control.setErrors({ serverError: error.error.errors[key] });
        }
      });
      this.toastr.error('Please correct the highlighted fields');
    } else if (error?.error?.message) {
      // Handle custom error messages from backend
      this.toastr.error(error.error.message);
    } else if (error?.message) {
      // Handle general error messages
      this.toastr.error(error.message);
    } else {
      // Fallback error message
      this.toastr.error(defaultMessage);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.companyForm.get(controlName);

    if (!control || !control.errors || !control.touched) return '';

    if (control.errors['required']) {
      return this.fieldErrors[controlName] || 'This field is required';
    }
    if (control.errors['serverError']) {
      return control.errors['serverError'];
    }
    if (control.errors['invalidSaudiPhone']) {
      return 'Please enter a valid Saudi phone number (e.g., 05XXXXXXXX or +9665XXXXXXXX)';
    }
    if (control.errors['invalidIban']) {
      return 'Please enter a valid Saudi IBAN (starting with SA)';
    }

    return this.fieldErrors[controlName] || 'Invalid value';
  }

  getButtonDisabledReason(): string {
    if (this.isSubmitting) return 'Currently submitting...';
    if (this.isLoading) return 'Loading company data...';
    if (this.hasRequiredFieldErrors()) {
      const requiredFields = this.getRequiredFieldErrors();
      return `Required fields missing: ${requiredFields.join(', ')}`;
    }
    if (this.hasValidationErrors()) {
      const invalidFields = this.getValidationErrors();
      return `Validation errors in: ${invalidFields.join(', ')}`;
    }
    return 'Ready to submit';
  }

  isButtonDisabled(): boolean {
    return this.isSubmitting || this.isLoading || this.hasRequiredFieldErrors();
  }

  private hasRequiredFieldErrors(): boolean {
    const requiredFields = ['name', 'crNumber', 'policyStartDate'];
    return requiredFields.some(field => {
      const control = this.companyForm.get(field);
      return !control?.value || control?.value.toString().trim() === '';
    });
  }

  private hasValidationErrors(): boolean {
    return Object.keys(this.companyForm.controls).some(key => {
      const control = this.companyForm.get(key);
      return control?.errors && !control.errors['serverError'];
    });
  }

  private getRequiredFieldErrors(): string[] {
    const requiredFields = ['name', 'crNumber', 'policyStartDate'];
    return requiredFields.filter(field => {
      const control = this.companyForm.get(field);
      return !control?.value || control?.value.toString().trim() === '';
    });
  }

  private getValidationErrors(): string[] {
    return Object.keys(this.companyForm.controls).filter(key => {
      const control = this.companyForm.get(key);
      return control?.errors && !control.errors['serverError'];
    });
  }

  // Add method to debug form state
  debugFormState(): void {
    console.log('Form valid:', this.companyForm.valid);
    console.log('Form status:', this.companyForm.status);
    console.log('Form errors:', this.getFormValidationErrors());
    console.log('Is submitting:', this.isSubmitting);
    console.log('Is loading:', this.isLoading);
  }

  private getFormValidationErrors(): any {
    const formErrors: any = {};
    Object.keys(this.companyForm.controls).forEach(key => {
      const controlErrors = this.companyForm.get(key)?.errors;
      if (controlErrors) {
        formErrors[key] = controlErrors;
      }
    });
    return formErrors;
  }

  private clearServerErrors(): void {
    Object.keys(this.companyForm.controls).forEach(key => {
      const control = this.companyForm.get(key);
      if (control?.errors?.['serverError']) {
        const errors = { ...control.errors };
        delete errors['serverError'];
        control.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    });
  }
}
